import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookCheck,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  RotateCcw,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import { formatCurrency, formatDateTime, formatRelative } from '@/lib/formatters';
import { useChartOfAccounts } from '@/features/financials/hooks';
import {
  buildCoaHierarchyByType,
  formatQbAccountLabel,
  QB_GROUP_ORDER,
} from '@/features/financials/coaHierarchy';
import {
  useApproveExpense,
  useExpenseTransaction,
  useReceiptSignedUrl,
  useRejectExpense,
  useRemoveReceipt,
  useSubmitExpense,
  useUnsubmitExpense,
  useUpdateExpenseCategory,
  useUpdateExpenseExplanation,
  useUploadReceipt,
} from '@/features/expenses/hooks';
import { ExpenseStatusBadge, ExpenseStatusEditor } from '@/features/expenses/ExpenseStatus';
import {
  SharedWithYouNote,
  TransactionSharingCard,
} from '@/features/expenses/TransactionSharingCard';
import { useTransactionAssignments } from '@/features/expenses/transactionAssignments';
import { useMyCardAssignments } from '@/features/expenses/cardAssignments';
import type { ExpenseStatus } from '@/types/database';

const QB_NONE = '__none__';

export function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { role, user } = useAuth();
  const superAdmin = isSuperAdmin(role);

  const { data: tx, isLoading, error } = useExpenseTransaction(id);
  const { data: coa } = useChartOfAccounts();

  // Sharing context: is the signed-in (non-super) user a per-transaction
  // assignee who isn't otherwise a cardholder? If so we show a "shared with
  // you" note so they understand why this row is in their queue.
  const { data: txAssignments } = useTransactionAssignments(id);
  const { data: myCardAssignments } = useMyCardAssignments();
  const isAssignee = (txAssignments ?? []).some((a) => a.profile_id === user?.id);
  const isCardholder =
    !!tx?.card_id &&
    (myCardAssignments ?? []).some((c) => c.card_id === tx.card_id);

  const updateExplanation = useUpdateExpenseExplanation();
  const updateCategory = useUpdateExpenseCategory();
  const submit = useSubmitExpense();
  const unsubmit = useUnsubmitExpense();
  const approve = useApproveExpense();
  const reject = useRejectExpense();
  const upload = useUploadReceipt();
  const removeReceipt = useRemoveReceipt();

  // Local draft for the explanation textarea so users can type freely
  // without firing a network request on every keystroke. Synced from the
  // server value when the transaction loads or refetches.
  const [explanation, setExplanation] = useState('');
  const lastServerExplanation = useRef<string | null>(null);
  useEffect(() => {
    if (tx && tx.expense_explanation !== lastServerExplanation.current) {
      lastServerExplanation.current = tx.expense_explanation;
      setExplanation(tx.expense_explanation ?? '');
    }
  }, [tx]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const qbAccountsByType = useMemo(() => buildCoaHierarchyByType(coa), [coa]);

  const { data: receiptUrl } = useReceiptSignedUrl(tx?.id, tx?.receipt_path ?? null);

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Expense" />
        <BackLink />
      </div>
    );
  }

  if (isLoading || !tx) {
    return (
      <div className="space-y-6">
        <PageHeader title="Loading expense…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = tx.expense_status;
  const isPosted = tx.qb_post_status === 'posted';
  // The expense is locked for editing when it's approved (anyone other
  // than super_admin), or when it's submitted and we're not super_admin.
  // It is locked for EVERYONE — including super_admin — once it's been
  // posted to QuickBooks: the row is now live in QBO and any field
  // change here would silently desync from the booked Purchase. To
  // change it, void/delete the Purchase in QBO first.
  const lockedForEmployee =
    !superAdmin && (status === 'submitted' || status === 'approved');
  const fieldsDisabled = lockedForEmployee || isPosted;

  const missing = {
    explanation: !tx.expense_explanation?.trim(),
    category: !tx.qb_account_id,
    receipt: !tx.receipt_path,
  };
  const ready = !missing.explanation && !missing.category && !missing.receipt;

  // ---------- Handlers ----------
  const onSaveExplanation = async () => {
    const next = explanation.trim() || null;
    if (next === (tx.expense_explanation ?? null)) return;
    try {
      await updateExplanation.mutateAsync({ id: tx.id, expense_explanation: next });
      toast.success('Explanation saved');
    } catch (e) {
      toast.error(messageOf(e, 'Save failed'));
    }
  };

  const onChangeCategory = async (next: string) => {
    const qb_account_id = next === QB_NONE ? null : next;
    try {
      await updateCategory.mutateAsync({ id: tx.id, qb_account_id });
      toast.success(qb_account_id ? 'Category updated' : 'Category cleared');
    } catch (e) {
      toast.error(messageOf(e, 'Update failed'));
    }
  };

  const onSubmit = async () => {
    try {
      // Save the in-progress explanation first so a change typed but not
      // blurred doesn't get lost behind the submit.
      const pending = explanation.trim() || null;
      if (pending !== (tx.expense_explanation ?? null)) {
        await updateExplanation.mutateAsync({ id: tx.id, expense_explanation: pending });
      }
      await submit.mutateAsync({ id: tx.id });
      toast.success('Submitted for approval');
    } catch (e) {
      toast.error(messageOf(e, 'Submit failed'));
    }
  };

  const onUnsubmit = async () => {
    try {
      await unsubmit.mutateAsync({ id: tx.id });
      toast.success('Returned to draft');
    } catch (e) {
      toast.error(messageOf(e, 'Could not withdraw'));
    }
  };

  const onApprove = async () => {
    try {
      // Save the in-progress explanation first so a change typed but not
      // blurred doesn't get lost behind the approve. Mirrors onSubmit so the
      // super_admin "fill in + approve in one step" flow on their own card
      // receipts behaves the same as the employee submit flow.
      const pending = explanation.trim() || null;
      if (pending !== (tx.expense_explanation ?? null)) {
        await updateExplanation.mutateAsync({ id: tx.id, expense_explanation: pending });
      }
      await approve.mutateAsync({ id: tx.id });
      toast.success('Approved. Ready to post to QuickBooks.');
    } catch (e) {
      toast.error(messageOf(e, 'Approval failed'));
    }
  };

  const onReject = async () => {
    try {
      await reject.mutateAsync({ id: tx.id, reason: rejectReason });
      toast.success('Rejected. The employee has been notified.');
      setRejectOpen(false);
      setRejectReason('');
    } catch (e) {
      toast.error(messageOf(e, 'Rejection failed'));
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same filename
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Receipt must be under 50 MB.');
      return;
    }
    try {
      await upload.mutateAsync({
        transactionId: tx.id,
        file,
        previousPath: tx.receipt_path,
      });
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error(messageOf(err, 'Upload failed'));
    }
  };

  const onRemoveReceipt = async () => {
    if (!tx.receipt_path) return;
    try {
      await removeReceipt.mutateAsync({ transactionId: tx.id, path: tx.receipt_path });
      toast.success('Receipt removed');
    } catch (e) {
      toast.error(messageOf(e, 'Could not remove receipt'));
    }
  };

  return (
    <div className="space-y-6">
      <BackLink />
      <PageHeader
        title={tx.description}
        meta={
          <>
            <span className="tabular-nums">{formatCurrency(tx.amount_cents)}</span>
            {' · '}
            {formatDateTime(tx.posted_at)}
            {tx.cards
              ? ` · ${tx.cards.name_on_card ?? 'Card'} •${tx.cards.last_four ?? '????'}`
              : tx.bank_accounts?.name
                ? ` · ${tx.bank_accounts.name}`
                : ''}
          </>
        }
        actions={
          superAdmin ? (
            <ExpenseStatusEditor
              transactionId={tx.id}
              status={status}
              qbPostStatus={tx.qb_post_status}
            />
          ) : (
            <ExpenseStatusBadge status={status} qbPostStatus={tx.qb_post_status} />
          )
        }
      />

      {/* Status banners */}
      {!superAdmin && (
        <SharedWithYouNote isCardholder={isCardholder} isAssignee={isAssignee} />
      )}
      {status === 'submitted' && !superAdmin && (
        <Banner tone="info" title="Awaiting approval" />
      )}
      {status === 'rejected' && (
        <Banner
          tone="warning"
          title={superAdmin ? 'Marked as rejected' : 'Your super_admin rejected this'}
        >
          {tx.rejection_reason ?? 'No reason was given. Please contact your super_admin.'}
        </Banner>
      )}
      {isPosted ? (
        <Banner tone="success" title="Posted to QuickBooks — taken care of">
          This expense is live in QuickBooks
          {tx.qb_external_id && (
            <>
              {' '}
              as Purchase{' '}
              <span className="font-mono text-foreground">{tx.qb_external_id}</span>
            </>
          )}
          {tx.qb_posted_at && <> · {formatRelative(tx.qb_posted_at)}</>}.
        </Banner>
      ) : status === 'approved' ? (
        <Banner tone="success" title="Approved">
          {tx.approved_at
            ? `Approved ${formatRelative(tx.approved_at)}`
            : 'Approved'}
          {tx.qb_post_status === 'pending' && (
            <span className="block pt-1 text-xs text-muted-foreground">
              Queued for QuickBooks.
            </span>
          )}
          {tx.qb_post_status === 'failed' && (
            <span className="block pt-1 text-xs text-fresh-salmon-dark">
              QuickBooks rejected the last post: {tx.qb_last_error ?? 'no detail captured'}
            </span>
          )}
        </Banner>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Form column */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Explanation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                onBlur={onSaveExplanation}
                placeholder="e.g. Annual subscription renewal for our project-management tool."
                rows={5}
                disabled={fieldsDisabled}
              />
              <div className="flex items-center justify-end text-xs text-muted-foreground">
                {!fieldsDisabled && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onSaveExplanation}
                    disabled={
                      updateExplanation.isPending ||
                      explanation.trim() === (tx.expense_explanation ?? '').trim()
                    }
                  >
                    Save
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>QuickBooks category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select
                value={tx.qb_account_id ?? QB_NONE}
                onValueChange={onChangeCategory}
                disabled={fieldsDisabled || updateCategory.isPending || (coa ?? []).length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      (coa ?? []).length === 0 ? 'Sync QuickBooks first…' : 'Pick a category'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={QB_NONE}>
                    <span className="text-muted-foreground">Unassigned</span>
                  </SelectItem>
                  {QB_GROUP_ORDER.map(({ key, label }) => {
                    const items = qbAccountsByType.get(key) ?? [];
                    if (items.length === 0) return null;
                    return (
                      <SelectGroup key={key}>
                        <SelectSeparator />
                        <SelectLabel>{label}</SelectLabel>
                        {items.map(({ account, depth, locked }) => (
                          <SelectItem
                            key={account.id}
                            value={account.id}
                            disabled={locked}
                            className={
                              locked
                                ? 'pl-8 text-muted-foreground/60 data-[disabled]:opacity-100'
                                : 'pl-8'
                            }
                            style={{ paddingLeft: `${2 + depth * 1.25}rem` }}
                          >
                            {formatQbAccountLabel(account)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Receipt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {tx.receipt_path ? (
                <ReceiptPreview
                  url={receiptUrl ?? null}
                  path={tx.receipt_path}
                  uploadedAt={tx.receipt_uploaded_at}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-secondary/30 p-6 text-center text-sm text-muted-foreground">
                  No receipt attached yet.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,application/pdf"
                  onChange={onFileSelected}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPickFile}
                  disabled={fieldsDisabled || upload.isPending}
                >
                  <Upload className="h-4 w-4" />
                  {upload.isPending
                    ? 'Uploading…'
                    : tx.receipt_path
                      ? 'Replace receipt'
                      : 'Upload receipt'}
                </Button>
                {tx.receipt_path && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onRemoveReceipt}
                    disabled={fieldsDisabled || removeReceipt.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action column */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!superAdmin && (status === 'draft' || status === 'rejected') && (
                <>
                  <Button
                    className="w-full"
                    onClick={onSubmit}
                    disabled={!ready || submit.isPending}
                  >
                    <Send className="h-4 w-4" />
                    {submit.isPending ? 'Submitting…' : 'Submit for approval'}
                  </Button>
                  {!ready && <ReadinessChecklist missing={missing} />}
                </>
              )}
              {!superAdmin && status === 'submitted' && (
                <Button className="w-full" variant="outline" onClick={onUnsubmit}>
                  <RotateCcw className="h-4 w-4" />
                  Withdraw to edit
                </Button>
              )}
              {superAdmin && (
                <SuperAdminActions
                  status={status}
                  isPosted={isPosted}
                  ready={ready}
                  missing={missing}
                  onApprove={onApprove}
                  onRejectOpen={() => setRejectOpen(true)}
                  onUnsubmit={onUnsubmit}
                  busy={approve.isPending || reject.isPending || unsubmit.isPending}
                />
              )}
            </CardContent>
          </Card>

          {superAdmin && tx.card_id && (
            <TransactionSharingCard
              transactionId={tx.id}
              cardId={tx.card_id}
              disabled={isPosted}
            />
          )}

          <Card>
            <CardHeader>
              <CardTitle>Audit trail</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-muted-foreground">
              <KV label="Submitted" value={tx.submitted_at ? formatRelative(tx.submitted_at) : '—'} />
              <KV label="Approved" value={tx.approved_at ? formatRelative(tx.approved_at) : '—'} />
              <KV
                label="Receipt uploaded"
                value={tx.receipt_uploaded_at ? formatRelative(tx.receipt_uploaded_at) : '—'}
              />
              <KV label="Synced" value={formatRelative(tx.created_at)} />
              {tx.qb_post_status === 'posted' && tx.qb_posted_at && (
                <KV label="Posted to QBO" value={formatRelative(tx.qb_posted_at)} />
              )}
              {tx.qb_post_status === 'failed' && tx.qb_last_error && (
                <KV label="QBO error" value={tx.qb_last_error} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Receipt is unreadable. Please upload a clearer copy."
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={!rejectReason.trim() || reject.isPending}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/financials/expenses"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to my expenses
    </Link>
  );
}

function Banner({
  tone,
  title,
  children,
}: {
  tone: 'info' | 'warning' | 'success';
  title: string;
  children?: React.ReactNode;
}) {
  const toneClass =
    tone === 'success'
      ? 'border-bright-moss-dark/30 bg-bright-moss-dark/10'
      : tone === 'warning'
        ? 'border-fresh-salmon-dark/30 bg-fresh-salmon-dark/10'
        : 'border-atmosphere-dark/30 bg-atmosphere-dark/10';
  return (
    <div className={`rounded-lg border ${toneClass} px-4 py-3 text-sm`}>
      <p className="font-medium text-foreground">{title}</p>
      {children != null && children !== false && (
        <p className="text-muted-foreground">{children}</p>
      )}
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

function SuperAdminActions({
  status,
  isPosted,
  ready,
  missing,
  onApprove,
  onRejectOpen,
  onUnsubmit,
  busy,
}: {
  status: ExpenseStatus;
  isPosted: boolean;
  ready: boolean;
  missing: { explanation: boolean; category: boolean; receipt: boolean };
  onApprove: () => void;
  onRejectOpen: () => void;
  onUnsubmit: () => void;
  busy: boolean;
}) {
  // Posted to QBO is a terminal state from Atlas's perspective. Don't
  // expose Approve / Reopen here — see the banner above for the reason.
  if (isPosted) {
    return (
      <p className="rounded-md border border-bright-moss-dark/30 bg-bright-moss-dark/10 px-3 py-2 text-xs text-muted-foreground">
        <ShieldCheck className="mr-1 inline h-3 w-3 text-bright-moss-dark" />
        Live in QuickBooks.
      </p>
    );
  }
  // 'in_qb' means the expense was booked in QuickBooks outside Atlas
  // (manual Purchase, journal entry, CSV import, …). The qb_status_sync
  // trigger pins qb_post_status to 'skipped', so isPosted is false even
  // though there's nothing for Atlas to approve / post. Flip back to
  // 'draft' via the status editor in the page header to re-enter the
  // normal lifecycle.
  if (status === 'in_qb') {
    return (
      <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        <BookCheck className="mr-1 inline h-3 w-3" />
        Booked in QuickBooks outside Atlas.
      </p>
    );
  }
  if (status === 'submitted') {
    return (
      <div className="space-y-2">
        <Button className="w-full" onClick={onApprove} disabled={!ready || busy}>
          <CheckCircle2 className="h-4 w-4" />
          {busy ? 'Working…' : 'Approve'}
        </Button>
        <Button
          className="w-full"
          variant="outline"
          onClick={onRejectOpen}
          disabled={busy}
        >
          <XCircle className="h-4 w-4" />
          Reject…
        </Button>
        {!ready && <ReadinessChecklist missing={missing} />}
      </div>
    );
  }
  if (status === 'approved') {
    return (
      <div className="space-y-2">
        <Button className="w-full" variant="outline" onClick={onUnsubmit} disabled={busy}>
          <RotateCcw className="h-4 w-4" />
          Reopen as draft
        </Button>
      </div>
    );
  }
  if (status === 'rejected') {
    // Super_admin can approve directly (e.g. the employee fixed the receipt
    // and pinged you in chat instead of resubmitting), or wait for the
    // employee to update and resubmit through the normal flow.
    return (
      <div className="space-y-2">
        <Button className="w-full" onClick={onApprove} disabled={!ready || busy}>
          <CheckCircle2 className="h-4 w-4" />
          {busy ? 'Working…' : 'Approve directly'}
        </Button>
        {!ready && <ReadinessChecklist missing={missing} />}
      </div>
    );
  }
  // Draft. Two cases handled by the same UI:
  //   1. Super_admin entering their OWN card receipt — there's nobody else
  //      to "submit to", so they fill in details and approve in one step.
  //   2. Super_admin reviewing an employee's unfinished draft — same button
  //      lets them push it through if the employee already filled it out.
  return (
    <div className="space-y-2">
      <Button className="w-full" onClick={onApprove} disabled={!ready || busy}>
        <CheckCircle2 className="h-4 w-4" />
        {busy ? 'Working…' : 'Approve directly'}
      </Button>
      {!ready && <ReadinessChecklist missing={missing} />}
    </div>
  );
}

function ReadinessChecklist({
  missing,
}: {
  missing: { explanation: boolean; category: boolean; receipt: boolean };
}) {
  return (
    <ul className="space-y-1 text-xs text-muted-foreground">
      {missing.explanation && <li>· Add an explanation</li>}
      {missing.category && <li>· Pick a category</li>}
      {missing.receipt && <li>· Upload a receipt</li>}
    </ul>
  );
}

function ReceiptPreview({
  url,
  path,
  uploadedAt,
}: {
  url: string | null;
  path: string;
  uploadedAt: string | null;
}) {
  const isImage = /\.(png|jpe?g|gif|heic|webp)$/i.test(path);
  const isPdf = /\.pdf$/i.test(path);
  return (
    <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-4">
      {url ? (
        isImage ? (
          <img
            src={url}
            alt="Receipt"
            className="mx-auto max-h-96 w-auto rounded-md border border-border"
          />
        ) : isPdf ? (
          <iframe
            src={url}
            title="Receipt PDF"
            className="h-96 w-full rounded-md border border-border bg-white"
          />
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            <span>Attached file (preview unavailable)</span>
          </div>
        )
      ) : (
        <Skeleton className="h-32 w-full" />
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {uploadedAt ? `Uploaded ${formatRelative(uploadedAt)}` : 'Uploaded'} ·{' '}
          {fileNameOf(path)}
        </span>
        {url && (
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-atmosphere-dark hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open
            </a>
            <a
              href={url}
              download
              className="inline-flex items-center gap-1 text-xs text-atmosphere-dark hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function fileNameOf(path: string): string {
  const last = path.split('/').pop() ?? path;
  // Strip the leading "<timestamp>-" we prepended at upload time so the
  // user sees their original filename in the preview.
  const m = last.match(/^\d+-(.+)$/);
  return m ? m[1] : last;
}

function messageOf(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message;
  return fallback;
}
